import { supabase } from "@/integrations/supabase/client";
import { findDaggersMissingSoleAsterisk, findOrphanedAsterisks } from "@smartmed/diagnosis-rules";
import { useQuery } from "@tanstack/react-query";

const NO_FLAGS = { orphanedAsterisks: new Set<string>(), daggersMissingAsterisk: new Set<string>() };

/** On-screen asterisk codes with none of their registered daggers, and on-screen daggers missing their only asterisk. */
export const useDaggerAsteriskChecks = (codes: readonly string[]) => {
  const sortedCodes = [...codes].sort();

  return useQuery({
    queryKey: ["dagger-asterisk-checks", sortedCodes],
    enabled: sortedCodes.length > 0,
    queryFn: async () => {
      const { data: codeRows, error: codeError } = await supabase
        .from("icd10_codes")
        .select("id, code")
        .in("code", sortedCodes);
      if (codeError) throw codeError;

      const presentIds = (codeRows ?? []).map((row) => row.id);
      const codeById = new Map((codeRows ?? []).map((row) => [row.id, row.code]));
      if (presentIds.length === 0) return NO_FLAGS;

      const idList = presentIds.join(",");
      const { data: bundleRows, error: bundleError } = await supabase
        .from("icd10_code_bundles")
        .select("primary_code_id, secondary_code_id")
        .eq("type", "dagger_asterisk")
        .or(`primary_code_id.in.(${idList}),secondary_code_id.in.(${idList})`);
      if (bundleError) throw bundleError;

      const bundlePairs = (bundleRows ?? []).map((row) => ({
        daggerCode: row.primary_code_id,
        asteriskCode: row.secondary_code_id,
      }));
      const orphanedIds = findOrphanedAsterisks({ present: new Set(presentIds), bundlePairs });
      const daggersMissingAsterisk = findDaggersMissingSoleAsterisk({
        codesInSequence: presentIds.map((id) => ({ code: id })),
        bundlePairs,
      });

      return {
        orphanedAsterisks: new Set(orphanedIds.flatMap((id) => codeById.get(id) ?? [])),
        daggersMissingAsterisk: new Set(
          daggersMissingAsterisk.flatMap(({ daggerCode }) => codeById.get(daggerCode) ?? []),
        ),
      };
    },
  });
};
